import type {
  ILoadingActions,
  ILoadingContext,
} from "@/types/context/LoadingContext";
import { createContext, useState } from "react";

// Create the context without default values
const LoadingContext = createContext<Partial<ILoadingContext>>({});

type LoadingContextProviderProps = {
  children: React.ReactNode;
};

const LoadingContextProvider = ({ children }: LoadingContextProviderProps) => {
  /**
   * Loading state/controls
   */

  /**
   * Show loading spinner
   * @param text
   * @param actions
   */
  const showLoading = (text?: string, actions?: ILoadingActions[]) => {
    toggleLoading((prevState) => {
      return {
        ...prevState,
        isLoading: true,
        loadingText: text,
        loadingActions: actions,
      };
    });
  };

  /**
   * Hide loading spinner
   */
  const hideLoading = () => {
    toggleLoading((prevState) => {
      return {
        ...prevState,
        isLoading: false,
        loadingText: null,
        loadingActions: [],
      };
    });
  };

  /**
   * Set Text for loading spinner
   * @param text
   */
  const setLoadingText = (text?: string) => {
    toggleLoading((prevState) => {
      return {
        ...prevState,
        loadingText: text,
      };
    });
  };

  /**
   * Set Actions for loading spinner
   * @param actions
   */
  const setLoadingActions = (actions?: ILoadingActions[]) => {
    toggleLoading((prevState) => {
      return {
        ...prevState,
        loadingActions: actions,
      };
    });
  };

  const loadingState: ILoadingContext = {
    isLoading: false,
    loadingText: null,
    loadingActions: [],
    showLoading,
    hideLoading,
    setLoadingText,
    setLoadingActions,
  };

  const [loading, toggleLoading] = useState<ILoadingContext>(loadingState);

  return (
    <LoadingContext.Provider value={loading}>
      {children}
    </LoadingContext.Provider>
  );
};

export { LoadingContextProvider, LoadingContext };
